import { memo, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ChevronDown, Minus, Plus, Trash2 } from 'lucide-react';

import { formatSeconds } from '../../utils/workoutStats';
import { ExerciseArt, WarmupTag } from './parts';
import { baseName, libraryFields, libraryTitle, searchLibrary, useExerciseLine } from './exerciseUtils';

const SERIES = { step: 1, min: 1, max: 10 };
const REPS = { step: 1, min: 1, max: 50 };
const LOAD = { step: 2.5, min: 0, max: 999 };
const REST = { step: 10, min: 10, max: 300 };

const clamp = (value, { min, max }) => Math.min(max, Math.max(min, value));

// "1:30" or "90" (seconds) → 90
const parseRest = (text) => {
  const [minutes, seconds] = String(text).split(':').map((part) => parseInt(part, 10));
  if (Number.isNaN(minutes)) return null;
  return seconds === undefined || Number.isNaN(seconds) ? minutes : minutes * 60 + seconds;
};

/** − value +: big targets for a phone in a sweaty hand; the value can be typed too */
const Stepper = ({ id, label, extra, value, inputMode = 'numeric', onStep, onChange, onFocus, onBlur }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-tr-num-field">
      <div className="mt-tr-num-field__head">
        <label htmlFor={id}>{label}</label>
        {extra}
      </div>
      <div className="mt-tr-num-field__ctl">
        <button type="button" onClick={() => onStep(-1)} aria-label={t('pages.Workout.editor.stepDown', { field: label.toLowerCase() })}>
          <Minus aria-hidden="true" />
        </button>
        <input
          id={id}
          value={value}
          inputMode={inputMode}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <button type="button" onClick={() => onStep(1)} aria-label={t('pages.Workout.editor.stepUp', { field: label.toLowerCase() })}>
          <Plus aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/**
 * One exercise in the editor: a row (art, name, sets × reps · load · rest) that opens in place, one at a time, to
 * edit its name (with library suggestions), its numbers, the warm-up mark and how it's done, or to swap it for
 * another from the library
 */
const ExerciseRowInner = ({ index, exercise, isOpen, isNameMissing, canRemove, library, onToggle, onChange, onRemove, onSwap }) => {
  const { t, i18n: { language } } = useTranslation();
  const isPt = language === 'pt';
  const card = (key, options) => t(`components.ExerciseCard.${key}`, options);
  const editor = (key, options) => t(`pages.Workout.editor.${key}`, options);
  const line = useExerciseLine();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [restDraft, setRestDraft] = useState(null);

  const key = exercise.uiKey ?? `position-${index}`;
  const bodyId = `mt-tr-erow-${key}`;
  const name = baseName(exercise);
  const warmupLabel = card('markAsWarmup');
  // The field shows the name as typed (trailing spaces too), without the warm-up suffix
  const suffix = ` (${warmupLabel})`;
  const rawName = exercise.name || '';
  const fieldName = exercise.warmup && rawName.endsWith(suffix) ? rawName.slice(0, -suffix.length) : rawName.replace(/\s*\((warmup|aquecimento)\)/gi, '');
  const update = (fields) => onChange(index, fields);

  const deferredName = useDeferredValue(fieldName);
  const suggestions = useMemo(() => {
    if (!showSuggestions || deferredName.trim().length < 2) return [];
    return searchLibrary(library, { query: deferredName, isPt }).slice(0, 5);
  }, [library, deferredName, showSuggestions, isPt]);

  const rename = (typed) => {
    update({
      name: exercise.warmup ? `${typed}${suffix}` : typed,
      // A typed name no longer matches the library entry: drop its media and instructions.
      // Warm-ups keep them, their "(Aquecimento)" suffix is intentional.
      ...(exercise.exerciseId && !exercise.warmup ? { videoUrl: '', thumbnailUrl: '', gifUrl: '', exerciseId: '', howTo: '' } : {}),
    });
    setShowSuggestions(true);
  };

  const pickSuggestion = (attrs) => {
    const fields = libraryFields(attrs, isPt);
    update({ ...fields, name: exercise.warmup ? `${fields.name}${suffix}` : fields.name });
    setShowSuggestions(false);
  };

  const toggleWarmup = () => update(exercise.warmup ? { warmup: false, name } : { warmup: true, name: `${name}${suffix}` });

  const stepCount = (field, limits) => (direction) => {
    const current = parseInt(exercise[field], 10) || 0;
    update({ [field]: String(clamp(current + direction * limits.step, limits)) });
  };

  const typeCount = (field) => (text) => update({ [field]: text.replace(/\D/g, '').slice(0, 3) });
  // An empty or zero count goes back to 1 when the field is left
  const settleCount = (field) => () => {
    if (!(parseInt(exercise[field], 10) > 0)) update({ [field]: '1' });
  };

  const loadValue = String(exercise.weight ?? '');
  const stepLoad = (direction) => {
    const current = parseFloat(loadValue.replace(',', '.')) || 0;
    const nextLoad = clamp(Math.round((current + direction * LOAD.step) * 10) / 10, LOAD);
    update({ weight: nextLoad ? String(nextLoad) : '' });
  };
  // Stored with a dot ("12.5"); typed and shown with the language's decimal mark
  const typeLoad = (text) => {
    const cleaned = text.replace(/[^\d.,]/g, '').replace(',', '.');
    const [whole, ...decimals] = cleaned.split('.');
    update({ weight: decimals.length ? `${whole}.${decimals.join('').slice(0, 2)}` : whole.slice(0, 4) });
  };

  const rest = Number(exercise.interval) || 60;
  // Above the 5:00 the steps reach, a saved rest (from the AI or older versions) is kept and shown as it is
  const stepRest = (direction) => update({ interval: clamp(rest + direction * REST.step, { min: REST.min, max: Math.max(REST.max, rest) }) });
  const settleRest = () => {
    const parsed = parseRest(restDraft ?? '');
    if (parsed !== null) update({ interval: Math.max(REST.min, parsed) });
    setRestDraft(null);
  };

  return (
    <li className={`mt-tr-erow${isOpen ? ' is-open' : ''}${isNameMissing ? ' is-bad' : ''}`}>
      <button type="button" className="mt-tr-erow__head" aria-expanded={isOpen} aria-controls={isOpen ? bodyId : undefined} onClick={() => onToggle(key)}>
        <ExerciseArt src={exercise.thumbnailUrl} />
        <span className="mt-tr-erow__text">
          <span className="mt-tr-erow__name">
            {name || editor('unnamed')}
            {exercise.warmup && <WarmupTag />}
          </span>
          <span className="mt-tr-erow__line">{line(exercise)}</span>
        </span>
        <ChevronDown className="mt-tr-erow__chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div id={bodyId} className="mt-tr-erow__body">
          <div className="mt-tr-erow__name-field">
            <label className="mt-tr-label" htmlFor={`${bodyId}-name`}>
              {editor('name')}
            </label>
            <input
              id={`${bodyId}-name`}
              className="mt-tr-field"
              value={fieldName}
              placeholder={card('namePlaceholder')}
              autoComplete="off"
              aria-invalid={isNameMissing || undefined}
              onChange={(event) => rename(event.target.value)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              onKeyDown={(event) => event.key === 'Escape' && setShowSuggestions(false)}
            />
            {suggestions.length > 0 && (
              <ul className="mt-tr-sugg" aria-label={card('suggestions')}>
                {suggestions.map(({ id, attributes: attrs }) => (
                  <li key={id}>
                    <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => pickSuggestion(attrs)}>
                      <ExerciseArt src={attrs.thumbnailUrl} />
                      {libraryTitle(attrs, isPt)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-tr-nums">
            <Stepper
              id={`${bodyId}-series`}
              label={card('series')}
              value={exercise.series ?? ''}
              onStep={stepCount('series', SERIES)}
              onChange={typeCount('series')}
              onBlur={settleCount('series')}
            />
            <Stepper
              id={`${bodyId}-reps`}
              label={card('reps')}
              value={exercise.reps ?? ''}
              onStep={stepCount('reps', REPS)}
              onChange={typeCount('reps')}
              onBlur={settleCount('reps')}
            />
            <Stepper
              id={`${bodyId}-load`}
              label={card('load')}
              inputMode="decimal"
              value={isPt ? loadValue.replace('.', ',') : loadValue}
              onStep={stepLoad}
              onChange={typeLoad}
              extra={
                <span className="mt-tr-unit" role="group" aria-label={editor('unit')}>
                  {['kg', 'lbs'].map((unit) => (
                    <button key={unit} type="button" aria-pressed={(exercise.weightUnit ?? 'kg') === unit} onClick={() => update({ weightUnit: unit })}>
                      {unit === 'lbs' ? 'lb' : 'kg'}
                    </button>
                  ))}
                </span>
              }
            />
            <Stepper
              id={`${bodyId}-rest`}
              label={card('rest')}
              inputMode="numeric"
              value={restDraft ?? formatSeconds(rest)}
              onStep={stepRest}
              onChange={(text) => setRestDraft(text.replace(/[^\d:]/g, '').slice(0, 5))}
              onFocus={() => setRestDraft(formatSeconds(rest))}
              onBlur={settleRest}
            />
          </div>

          <label className="mt-tr-switch-row">
            <span>{card('warmupSwitch')}</span>
            <input type="checkbox" role="switch" className="mt-tr-switch" checked={!!exercise.warmup} onChange={toggleWarmup} />
          </label>

          <div>
            <label className="mt-tr-label" htmlFor={`${bodyId}-howto`}>
              {card('executionLabel')}
            </label>
            <textarea
              id={`${bodyId}-howto`}
              className="mt-tr-field mt-tr-field--area"
              rows={3}
              value={exercise.howTo ?? ''}
              placeholder={editor('howToPlaceholder')}
              onChange={(event) => update({ howTo: event.target.value })}
            />
          </div>

          <div className="mt-tr-erow__acts">
            <button type="button" className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--xs" onClick={() => onSwap(index)}>
              <ArrowLeftRight aria-hidden="true" />
              {editor('swap')}
            </button>
            <button
              type="button"
              className="mt-tr-text-btn mt-tr-text-btn--danger"
              onClick={() => onRemove(index)}
              disabled={!canRemove}
              aria-label={card('deleteNamed', { name: name || card('exercise') })}
            >
              <Trash2 aria-hidden="true" />
              {editor('remove')}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

export const ExerciseRow = memo(ExerciseRowInner);
